import { Box, Flex, Textarea } from '@chakra-ui/react'
import React from 'react'
import { easeInOutTransition } from '~/components/CommonStyles'
import { Dropdown } from '~/components/core'
import { themeColorSectionKeys } from '~/themes/colors/colors'

const {
  BorderColor,
  BackgroundColor,
  BackgroundColor3,
  MaxCountColor,
  MaxCountColor2,
  LoadingColor,
  isSelectedColor,
  BackgroundColor2,
} = themeColorSectionKeys.Settings.SnippetSettings

export const RootWrapper = (props) => (
  <Flex
    width="100%"
    height="100%"
    padding="2.5rem"
    paddingTop="0"
    flexDirection="column"
    borderRadius="0.375rem"
    borderLeft="2px"
    borderLeftColor={BorderColor}
    {...props}
  />
)

export const HeaderWrapper = (props) => (
  <Flex
    width="100%"
    padding="0.5rem"
    paddingX="0.5rem"
    paddingTop="1.875rem"
    alignItems="center"
    justifyContent="space-between"
    borderBottom="1px"
    borderBottomColor={BorderColor}
    {...props}
  />
)

export const ErrorWrapper = (props) => {
  return (
    <Flex
      width="100%"
      padding="0.75rem"
      alignItems="center"
      justifyContent="space-between"
      backgroundColor={BackgroundColor}
      borderBottom="1px"
      borderBottomColor={BorderColor}
      {...props}
    />
  )
}

export const HeaderTitleWrapper = (props) => {
  return (
    <Flex
      alignItems="center"
      sx={{
        '> p': {
          marginLeft: '0.5rem',
        },
      }}
      {...props}
    />
  )
}

export const EditTitleWrapper = (props) => {
  return (
    <Flex
      alignItems="center"
      sx={{
        '> p': {
          marginRight: '0.25rem',
        },
      }}
      {...props}
    />
  )
}

export const HeaderActionWrapper = (props) => {
  return (
    <Flex
      alignItems="center"
      sx={{
        '> div:last-child': {
          marginLeft: '0.625rem',
        },
      }}
      {...props}
    />
  )
}

export const ListWrapper = (props) => {
  return (
    <Box
      width="100%"
      height="100%"
      overflowY="auto"
      border="1px"
      borderColor={BorderColor}
      borderRadius="0.375rem"
      backgroundColor={BackgroundColor}
      {...props}
    />
  )
}

export const ItemWrapper = (props) => {
  return (
    <Flex
      width="100%"
      height="2.68rem"
      padding="0.5rem 0.75rem"
      alignItems="center"
      justifyContent="space-between"
      backgroundColor={BackgroundColor}
      borderBottom="1px"
      borderBottomColor={BorderColor}
      cursor="pointer"
      transition={easeInOutTransition}
      _hover={{ backgroundColor: BorderColor }}
      _active={{ backgroundColor: BackgroundColor3 }}
      _last={{
        borderBottom: 'none',
      }}
      {...props}
    />
  )
}

export const ItemNameWrapper = (props) => {
  return <Flex width="calc(100% - 60px)" {...props} />
}

export const ItemActionWrapper = (props) => {
  return <Flex alignItems="center" {...props} />
}

export const EditContentWrapper = (props) => {
  return (
    <Flex
      width="100%"
      height="calc(100% - 4.5rem)"
      padding="0.75rem"
      flexDirection="column"
      {...props}
    />
  )
}

export const ShortcutNameInputWrapper = (props) => {
  return <Flex flexDirection="column" width="100%" {...props} />
}

export const GroupDropdownWrapper = (props) => {
  return (
    <Flex
      width="100%"
      display="block"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      borderRadius="0.375rem"
      flexDirection="column"
      {...props}
    />
  )
}

export const SnippetDropdown = (props) => {
  return (
    <Dropdown
      border="1px"
      borderColor={BorderColor}
      height="160px"
      overflow="scroll"
      {...props}
    />
  )
}

interface EditBodyInputAreaProps {
  hasExceededMaxCount: boolean
  value: string
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  placeholder?: string
}

export const EditBodyInputArea = ({
  hasExceededMaxCount,
  placeholder,
  onChange,
  ...props
}: EditBodyInputAreaProps) => {
  const borderColor = hasExceededMaxCount ? MaxCountColor : MaxCountColor2

  return (
    <Textarea
      width="100%"
      height="100%"
      padding="0.5rem"
      fontSize="13px"
      lineHeight="17px"
      backgroundColor={BackgroundColor}
      borderRadius="0.375rem"
      border="1px"
      borderColor={borderColor}
      resize="none"
      verticalAlign="top"
      placeholder={placeholder}
      onChange={onChange}
      {...props}
    />
  )
}

export const EditFooterWrapper = (props) => {
  return (
    <Flex
      width="100%"
      height="3.5rem"
      alignItems="center"
      justifyContent="flex-end"
      borderTop="2px"
      borderTopColor={BorderColor}
      paddingRight="0.75rem"
      {...props}
    />
  )
}

export const CancelButtonWrapper = (props) => {
  return <Flex paddingRight="0.25rem" {...props} />
}

export const TextAreaWrapper = (props) => {
  return <Flex position="relative" height="calc(100% - 208px)" {...props} />
}

export const HelperText = (props) => {
  return <Flex margin="0.25rem 0 0.75rem 0" {...props} />
}

export const SnippetLabel = (props) => {
  return <Flex margin="0.25rem 0" {...props} />
}

export const FilterMenuWrapper = (props) => (
  <Flex
    width="100%"
    padding="0.75rem"
    alignItems="center"
    justifyContent="space-between"
    backgroundColor={BackgroundColor}
    borderRadius="0.375rem 0.375rem 0 0"
    border="1px"
    borderColor={BorderColor}
    cursor="pointer"
    transition={easeInOutTransition}
    {...props}
  />
)

export const LoadingWrapper = (props) => {
  return <Flex height="100%" {...props} />
}

export const Loading = (props) => {
  return (
    <Flex
      height="100%"
      alignItems="center"
      justifyContent="center"
      backgroundColor={BackgroundColor}
      color={LoadingColor}
      userSelect="none"
      {...props}
    />
  )
}

export const EmptyStateWrapper = (props) => (
  <Flex
    height="100%"
    alignItems="center"
    justifyContent="center"
    backgroundColor={BackgroundColor}
    {...props}
  />
)

export const FilterOption = ({ isSelected, ...props }) => (
  <Box
    flex={1}
    borderRadius="md"
    p={1}
    bg={isSelected ? BorderColor : isSelectedColor}
    cursor="pointer"
    transition="background 200ms ease-in-out"
    _hover={!isSelected ? { bg: BackgroundColor2 } : {}}
    _active={!isSelected ? { bg: BackgroundColor2 } : {}}
    textAlign="center"
    lineHeight="1.75"
    {...props}
  />
)
export const FilterOptionWrapper = (props) => (
  <Flex width="100%" gap={2} {...props} />
)
